import { useState } from 'react';
import { CloudUpload, Download, FolderDown, Loader2, Send, Trash2, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { BACKEND_URL } from '../../lib/backend';

export function ExportsPanel({ exports, orders, onRefresh, onDelivered }) {
  const [deliveringId, setDeliveringId] = useState(null);
  const [zippingId, setZippingId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState({});
  const deliverable = orders.filter((o) => o.status === 'accepted' && !o.cancel_requested);

  const handleZip = async (exp) => {
    setZippingId(exp.id);
    try {
      const res = await api.downloadStudioExportZip(exp.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exp.name}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Could not build the zip. Please try again.');
    } finally {
      setZippingId(null);
    }
  };

  const handleDeliver = async (exp) => {
    const orderId = selectedOrder[exp.id];
    if (!orderId) { toast.error('Pick an order to deliver to'); return; }
    setDeliveringId(exp.id);
    try {
      await api.deliverStudioExport(exp.id, orderId);
      toast.success('Dubplate delivered!');
      onDelivered();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delivery failed');
    } finally {
      setDeliveringId(null);
    }
  };

  const handleDelete = async (exp) => {
    if (!window.confirm(`Delete "${exp.name}"? This removes the saved stems.`)) return;
    try {
      await api.deleteStudioExport(exp.id);
      toast.success('Deleted');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-3" data-testid="studio-exports-panel">
      <h3 className="text-sm font-semibold flex items-center gap-2">
        <CloudUpload className="w-4 h-4 text-lime" />My Exports
      </h3>
      {exports.length === 0 ? (
        <p className="text-xs text-[#666] py-4 text-center">
          No saved sessions yet. Record a take and hit Export Stems.
        </p>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {exports.map((exp) => (
            <div key={exp.id} className="p-2.5 rounded-lg bg-[#111] border border-[#222] space-y-2" data-testid={`studio-export-${exp.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  <p className="text-xs font-medium truncate">{exp.name}</p>
                  {exp.delivered_at && (
                    <span
                      className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded-full bg-lime/15 text-lime border border-lime/30 text-[9px] font-semibold uppercase tracking-wide"
                      title={exp.delivered_song_title ? `Delivered to "${exp.delivered_song_title}"` : 'Delivered'}
                      data-testid={`delivered-badge-${exp.id}`}
                    >
                      <CheckCircle2 className="w-2.5 h-2.5" />Delivered
                    </span>
                  )}
                </div>
                <button onClick={() => handleDelete(exp)} className="text-[#666] hover:text-red-400 shrink-0" data-testid={`delete-export-${exp.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 text-[10px]">
                <button
                  onClick={() => handleZip(exp)}
                  disabled={zippingId === exp.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-lime/10 text-lime hover:bg-lime/20 border border-lime/30 font-medium disabled:opacity-60"
                  data-testid={`download-all-${exp.id}`}
                >
                  {zippingId === exp.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FolderDown className="w-3 h-3" />}
                  All (.zip)
                </button>
                {[
                  ['Mixdown', exp.mixdown_url],
                  ['Vocal', exp.vocal_url],
                  ['Instr', exp.instrumental_url],
                ].filter(([, url]) => url).map(([label, url]) => (
                  <a
                    key={label}
                    href={`${BACKEND_URL}${url}?dl=${encodeURIComponent(`${exp.name} - ${label}`)}`}
                    download
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#1a1a1a] text-[#A1A1AA] hover:text-lime border border-[#2a2a2a]"
                    data-testid={`download-${label.toLowerCase()}-${exp.id}`}
                  >
                    <Download className="w-3 h-3" />{label}
                  </a>
                ))}
              </div>
              {deliverable.length > 0 && (
                <div className="flex gap-1.5">
                  <select
                    value={selectedOrder[exp.id] || ''}
                    onChange={(e) => setSelectedOrder((p) => ({ ...p, [exp.id]: e.target.value }))}
                    className="flex-1 text-[11px] bg-[#0a0a0a] border border-[#2a2a2a] rounded px-1.5 py-1 text-[#ccc] min-w-0"
                    data-testid={`export-order-select-${exp.id}`}
                  >
                    <option value="">Deliver to order…</option>
                    {deliverable.map((o) => (
                      <option key={o.id} value={o.id}>{o.song_title || 'Untitled'} — {o.user_name || o.user_email}</option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    className="btn-primary h-7 text-[11px] px-2"
                    disabled={deliveringId === exp.id}
                    onClick={() => handleDeliver(exp)}
                    data-testid={`deliver-export-${exp.id}`}
                  >
                    {deliveringId === exp.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Send className="w-3 h-3 mr-1" />Deliver</>}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
